/**
 * WebGPU-compatible fat lines (Line2NodeMaterial + LineSegments2).
 * Classic LineMaterial is not supported by three's NodeBuilder / WebGPURenderer.
 */
import { Line2NodeMaterial } from "three/webgpu";
import { LineSegments2 } from "three/examples/jsm/lines/webgpu/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import {
  RENDER_SETTINGS_CHANGED_EVENT,
  renderSettingsStore,
} from "../../core/pipeline/RenderSettingsStore";

export type FatLineMaterial = Line2NodeMaterial;
export { LineSegments2, LineSegmentsGeometry };

export type FatLineMaterialOptions = {
  color: number;
  linewidth: number;
  depthTest?: boolean;
  transparent?: boolean;
  opacity?: number;
  alphaToCoverage?: boolean;
  dashed?: boolean;
  dashSize?: number;
  gapSize?: number;
};

const _scaledWires = new Set<Line2NodeMaterial>();
const _baseLineWidth = new WeakMap<Line2NodeMaterial, number>();
let _scaleListener = false;

/** 1 ekran pikseli, düşük pixelRatioScale'de bir tampon pikselinden ince kalmasın. */
function pixelRatioLineScale(): number {
  const scale = renderSettingsStore.value.pixelRatioScale;
  return 1 / Math.min(1, Math.max(0.25, scale));
}

function applyPixelRatioLineWidth(material: Line2NodeMaterial): void {
  const base = _baseLineWidth.get(material) ?? 1;
  material.linewidth = base * pixelRatioLineScale();
}

function ensurePixelRatioLineScale(): void {
  if (_scaleListener) return;
  _scaleListener = true;
  renderSettingsStore.addEventListener(RENDER_SETTINGS_CHANGED_EVENT, () => {
    for (const material of _scaledWires) applyPixelRatioLineWidth(material);
  });
}

export function createFatLineMaterial(
  options: FatLineMaterialOptions,
): Line2NodeMaterial {
  const material = new Line2NodeMaterial({
    color: options.color,
    linewidth: options.linewidth,
    depthTest: options.depthTest ?? false,
    transparent: options.transparent ?? true,
    opacity: options.opacity ?? 1,
    alphaToCoverage: options.alphaToCoverage ?? true,
    dashed: options.dashed ?? false,
  });
  // Line2NodeMaterial dash uniforms MaterialNode üzerinden okunur.
  const dashable = material as Line2NodeMaterial & {
    dashSize?: number;
    gapSize?: number;
  };
  if (options.dashSize != null) dashable.dashSize = options.dashSize;
  if (options.gapSize != null) dashable.gapSize = options.gapSize;
  return material;
}

export function createFatWireSegments(
  positions: number[],
  color: number,
  linewidth: number,
  renderOrder: number,
): LineSegments2 {
  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(positions);

  const material = createFatLineMaterial({
    color,
    linewidth,
    transparent: true,
    opacity: 1,
    depthTest: false,
    alphaToCoverage: false,
  });
  material.depthWrite = false;
  _baseLineWidth.set(material, linewidth);
  _scaledWires.add(material);
  ensurePixelRatioLineScale();
  applyPixelRatioLineWidth(material);
  const lines = new LineSegments2(geometry, material);
  lines.renderOrder = renderOrder;
  lines.frustumCulled = false;
  return lines;
}

export function disposeFatWireLines(lines?: LineSegments2): void {
  if (!lines) return;
  lines.geometry.dispose();
  const material = lines.material as Line2NodeMaterial;
  _scaledWires.delete(material);
  material.dispose();
  lines.removeFromParent();
}
